import { ref, computed } from 'vue';
import axios from 'axios';

export function useCourseData() {
  const loading = ref(true);
  const instructors = ref([]);
  const categories = ref([]);
  const courseForm = ref({});
  const originalCourseFormState = ref(null);
  const rawCourseData = ref(null);

  const normalizedInstructors = computed(() => {
    if (!Array.isArray(instructors.value)) return [];
    
    return instructors.value.map(instructor => ({
      ...instructor,
      user_id: instructor.user_id ? String(instructor.user_id) : '',
      display_name: `${instructor.first_name || ''} ${instructor.last_name || ''}`.trim()
    }));
  });

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://api.kewaar.com/api/admin/coursecategories2', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (response.data.success) {
        categories.value = response.data.data.map(category => ({
          id: category.id,
          name: category.title || category.title_ar,
          title_ar: category.title_ar
        }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchInstructors = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('https://api.kewaar.com/api/admin/instructors1', {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Accept': 'application/json'
        }
      });
      
      if (response.data.success) {
        instructors.value = Array.isArray(response.data.data) ? response.data.data : [];
      } else {
        instructors.value = [];
      }
    } catch (error) {
      console.error('Error fetching instructors:', error);
      instructors.value = [];
    }
  };

  const preprocessRichText = (htmlContent) => {
    if (!htmlContent) return '';
    
    let processedContent = htmlContent.replace(/<[^>]*>/g, match => {
      return '###HTML_TAG###' + encodeURIComponent(match) + '###HTML_TAG###';
    });
    
    processedContent = processedContent.replace(/\r\n|\n/g, '<br/>');
    
    processedContent = processedContent.replace(/###HTML_TAG###(.*?)###HTML_TAG###/g, (match, p1) => {
      return decodeURIComponent(p1);
    });
    
    if (!processedContent.includes('color:')) {
      processedContent = `<div style="color: #1e293b;">${processedContent}</div>`;
    }
    
    return processedContent;
  };

  const fetchCourse = async (courseId) => {
    try {
      loading.value = true;
      const token = localStorage.getItem('token');
      const response = await axios.get(`https://api.kewaar.com/api/admin/courses2/${courseId}`, {
        headers: { 'Authorization': `Bearer ${token}`, 'Accept': 'application/json' }
      });

      if (response.data.success && response.data.data) {
        const fetchedData = response.data.data;
        rawCourseData.value = JSON.parse(JSON.stringify(fetchedData));

        courseForm.value = {
          title: fetchedData.title || '',
          title_ar: fetchedData.title_ar || '',
          description: preprocessRichText(fetchedData.description || ''),
          description_ar: preprocessRichText(fetchedData.description_ar || ''),
          instructor_id: fetchedData.instructor_id ? String(fetchedData.instructor_id) : '',
          category_id: fetchedData.category_id ? String(fetchedData.category_id) : '',
          minutes: fetchedData.minutes ? String(fetchedData.minutes) : '',
          status: fetchedData.status || 'active',
          is_premium: Boolean(fetchedData.is_premium),
          price: fetchedData.price !== null ? String(fetchedData.price) : '',
          science_behind_it: fetchedData.science_behind_it || '',
          audio_cover: fetchedData.audio_cover || null,
          audio_cover_ar: fetchedData.audio_cover_ar || null,
          audio_url: fetchedData.audio_url || null,
          audio_url_ar: fetchedData.audio_url_ar || null,
          is_sleeping: Boolean(fetchedData.is_sleeping),
          sleep_id: fetchedData.sleep_id || null,
          preview_status: Boolean(fetchedData.preview_status),
          position_day: fetchedData.position_day !== null ? String(fetchedData.position_day) : '0',
          position_night: fetchedData.position_night !== null ? String(fetchedData.position_night) : '0',
          top_course: Boolean(fetchedData.top_course),
          is_breathing: Boolean(fetchedData.is_breathing),
          related_field: fetchedData.related_field || null,
          thumbnail: fetchedData.thumbnail || '',
          is_active: fetchedData.is_active !== null ? Boolean(fetchedData.is_active) : true
        };

        // Store original state for comparison
        setTimeout(() => {
          originalCourseFormState.value = JSON.parse(JSON.stringify(courseForm.value));
        }, 100);
      }
    } catch (error) {
      console.error('Error fetching course:', error);
      throw error;
    } finally {
      loading.value = false;
    }
  };

  const updateCourse = async (courseId, emit) => {
    try {
      if (!courseForm.value.title || !courseForm.value.category_id) {
        throw new Error('Title and Category are required.');
      }

      const updatePayload = {};
      
      // Compare changes and build payload
      for (const key in courseForm.value) {
        let changed = false;
        
        if (key === 'description' || key === 'description_ar') {
          const currentHtml = courseForm.value[key] || '';
          const originalHtml = rawCourseData.value ? rawCourseData.value[key] || '' : '';
          if (currentHtml !== originalHtml) {
            changed = true;
          }
        } else {
          const currentValue = courseForm.value[key];
          const originalValue = originalCourseFormState.value?.[key];
          
          if (JSON.stringify(currentValue) !== JSON.stringify(originalValue)) {
            changed = true;
          }
        }
        
        if (changed) {
          let value = courseForm.value[key];
          
          // Format value based on field type
          if (['instructor_id', 'category_id'].includes(key)) {
            value = value ? Number(value) : null;
          } else if (['minutes', 'position_day', 'position_night'].includes(key)) {
            value = value !== null && value !== '' ? Number(value) : null;
          } else if (key === 'price') {
            value = value !== null && value !== '' && !isNaN(Number(value)) ? Number(value) : null;
          } else if (['is_premium', 'is_active', 'preview_status', 'top_course', 'is_breathing', 'is_sleeping'].includes(key)) {
            value = Boolean(value);
          } else if (['title_ar', 'description', 'description_ar', 'science_behind_it', 'audio_cover', 'audio_cover_ar', 'audio_url', 'audio_url_ar', 'sleep_id', 'related_field', 'thumbnail'].includes(key)) {
            value = value || null;
          }
          
          updatePayload[key] = value;
        }
      }
      
      if (Object.keys(updatePayload).length === 0) {
        emit('show-notification', { type: 'info', message: 'No changes detected to update.' });
        return;
      }
      
      const token = localStorage.getItem('token');
      await axios({
        method: 'put',
        url: `https://api.kewaar.com/api/admin/courses2/${courseId}`,
        data: updatePayload,
        headers: { 
          'Authorization': `Bearer ${token}`, 
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      emit('show-notification', { type: 'success', message: 'Course updated successfully' });
      
      // Refresh the course data
      await fetchCourse(courseId);
      
    } catch (error) {
      console.error('Error updating course:', error);
      throw error;
    }
  };

  return {
    loading,
    instructors,
    categories,
    courseForm,
    originalCourseFormState,
    rawCourseData,
    normalizedInstructors,
    fetchCategories,
    fetchInstructors,
    fetchCourse,
    updateCourse
  };
} 